import React, { useState, useEffect, type FormEvent } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    InputAdornment
} from '@mui/material';
import { ApiRequestNoLoder as apiCall } from '../services/api';

interface LoginProps {
    onLogin: (email: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
    const [username, setUsername] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        console.log('Blackboard Login Component Mounted');
    }, []);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!otpSent && !username.trim()) return;
        if (otpSent && !otp.trim()) return;

        setLoading(true);
        setError('');

        try {
            if (!otpSent) {
                const fullEmail = `${username.trim()}@delightrg.com`;
                const result = await (apiCall as any)('POST', 'api/oladec/auth/oladec/send-otp', { username: fullEmail });

                if (result && result.success) {
                    setEmail(fullEmail);
                    setOtpSent(true);
                } else {
                    setError(result?.message || 'Failed to send OTP');
                }
            } else {
                const result = await (apiCall as any)('POST', 'api/oladec/auth/oladec/verify-otp', { username: email, otp });

                if (result && result.success) {
                    onLogin(email);
                } else {
                    setError(result?.message || 'Invalid OTP');
                }
            }
        } catch (error) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                width: '100vw',
                height: '100vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#121212',
                overflow: 'hidden'
            }}
        >
            {/* Wooden Frame Blackboard Container */}
            <Box sx={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
                height: { xs: '85%', md: '70%' },
                border: { xs: '12px solid #d4a373', md: '24px solid #d4a373' },
                borderRadius: '4px',
                backgroundColor: '#2b2b2b',
                boxShadow: `
                    0 20px 50px rgba(0,0,0,0.5), 
                    inset 0 0 40px rgba(0,0,0,0.5),
                    0 0 0 2px rgba(60, 40, 20, 0.3)
                `,
                backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 2px, transparent 2px, transparent 4px)',
            }}>
                <Paper
                    elevation={10}
                    sx={{
                        padding: { xs: '24px', md: '40px' },
                        borderRadius: '20px',
                        width: '100%',
                        maxWidth: '450px',
                        textAlign: 'center',
                        backgroundColor: '#1e1e28',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        color: '#fff',
                        boxShadow: '0 15px 35px rgba(0, 0, 0, 0.4)',
                        margin: '0 10px',
                    }}
                >
                    <Typography variant="h4" component="h2" sx={{
                        marginBottom: '10px',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', md: '2rem' },
                        letterSpacing: '-0.5px'
                    }}>
                        Welcome Back
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', marginBottom: { xs: '20px', md: '30px' }, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                        {otpSent ? `OTP sent to ${email}` : 'Please sign in to your Audit account'}
                    </Typography>

                    {error && (
                        <Alert severity="error" sx={{
                            marginBottom: '20px',
                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                            color: '#ffcdd2',
                            border: '1px solid rgba(211, 47, 47, 0.3)'
                        }}>
                            {error}
                        </Alert>
                    )}

                    <form autoComplete="off" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {!otpSent ? (
                            <TextField
                                fullWidth
                                label="Username"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={loading}
                                variant="outlined"
                                placeholder="username"
                                autoComplete="off"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Typography sx={{ color: '#e0e0e0', fontWeight: 500, fontSize: '0.95rem', userSelect: 'none' }}>
                                                @delightrg.com
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                                        '&.Mui-focused fieldset': { borderColor: '#646cff' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#646cff' }
                                }}
                            />
                        ) : (
                            <TextField
                                fullWidth
                                label="Enter OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value)}
                                disabled={loading}
                                variant="outlined"
                                placeholder="••••••"
                                autoComplete="off"
                                slotProps={{ htmlInput: { maxLength: 6 } }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        color: '#fff',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                                        '&.Mui-focused fieldset': { borderColor: '#646cff' },
                                    },
                                    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
                                    '& .MuiInputLabel-root.Mui-focused': { color: '#646cff' }
                                }}
                            />
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={loading}
                            sx={{
                                height: '50px',
                                fontWeight: 800,
                                fontSize: '1rem',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #646cff 0%, #535bf2 100%)',
                                color: 'white',
                                textTransform: 'none',
                                boxShadow: '0 5px 15px rgba(100, 108, 255, 0.4)',
                                '&:hover': {
                                    boxShadow: '0 5px 15px rgba(100, 108, 255, 0.6)',
                                    transform: 'translateY(-2px)'
                                },
                                '&:disabled': { opacity: 0.7 }
                            }}
                        >
                            {loading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                                    <CircularProgress size={20} sx={{ color: 'white' }} />
                                    <span>{otpSent ? 'Verifying...' : 'Sending...'}</span>
                                </Box>
                            ) : (
                                otpSent ? 'Verify OTP' : 'Get Access'
                            )}
                        </Button>

                        {otpSent && (
                            <Button
                                type="button"
                                variant="text"
                                onClick={() => {
                                    setOtpSent(false);
                                    setOtp('');
                                    setError('');
                                }}
                                disabled={loading}
                                sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none', '&:hover': { color: '#fff' } }}
                            >
                                Back to Email
                            </Button>
                        )}
                    </form>
                </Paper>
            </Box>

            {/* 3D-ish Shopping Cart SVG Container */}
            <Box
                sx={{
                    position: 'absolute',
                    bottom: { xs: '20px', md: '40px' },
                    right: { xs: '20px', md: '60px' },
                    zIndex: 10,
                    display: { xs: 'none', md: 'block' },
                    animation: 'slideInCart 3s ease-out forwards',
                    '@keyframes slideInCart': {
                        from: { transform: 'translateX(-100vw)' },
                        to: { transform: 'translateX(0)' }
                    }
                }}
            >
                <Box
                    sx={{
                        width: { xs: '150px', md: '250px' },
                        height: { xs: '150px', md: '250px' },
                        filter: 'drop-shadow(10px 10px 20px rgba(0,0,0,0.5))',
                        transform: 'rotate(-5deg)',
                        animation: 'float 6s ease-in-out infinite',
                        '@keyframes float': {
                            '0%, 100%': { transform: 'translateY(0) rotate(-5deg)' },
                            '50%': { transform: 'translateY(-10px) rotate(-3deg)' }
                        }
                    }}
                >
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1H5.76L6.46 4.39M6.46 4.39L7.38 8.86L8.4 13.79C8.59 14.7 9.39 15.36 10.32 15.36H18.9C19.8 15.36 20.58 14.74 20.8 13.86L22.25 8.12C22.42 7.42 21.89 6.74 21.17 6.74H7.81M6.46 4.39H20.5" stroke="#e0e0e0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="9" cy="20" r="1.5" fill="#ff4d4d" />
                        <circle cx="19" cy="20" r="1.5" fill="#ff4d4d" />
                        <path d="M18 15.36V16.5" stroke="#e0e0e0" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M9 15.36V16.5" stroke="#e0e0e0" strokeWidth="1.5" strokeLinecap="round" />
                        <path d="M7 8H21" stroke="#e0e0e0" strokeWidth="1" strokeOpacity="0.5" />
                        <path d="M7.5 11H20.5" stroke="#e0e0e0" strokeWidth="1" strokeOpacity="0.5" />
                        <path d="M11 5V15" stroke="#e0e0e0" strokeWidth="1" strokeOpacity="0.5" />
                        <path d="M14 5V15" stroke="#e0e0e0" strokeWidth="1" strokeOpacity="0.5" />
                        <path d="M17 5V15" stroke="#e0e0e0" strokeWidth="1" strokeOpacity="0.5" />
                        <path d="M21.17 6.74L20.5 4" stroke="#ff4d4d" strokeWidth="2" strokeLinecap="round" />
                        <rect x="8" y="8" width="5" height="4" rx="1" fill="#ff4d4d" transform="rotate(-15 10.5 10)" />
                    </svg>
                </Box>
            </Box>
        </Box>
    );
};

export default Login;
